import React, { useState, useEffect, useMemo } from "react";
import locale from "localization/en";
import styles from "../cash-noncash.module.scss";
import { Title, Image, Dropdown, Field, Button, Text, TextField } from "components/commons";
import { FormMode } from "enums";
import { RemoveIcon, StocqClockIn } from "images";
import { useMount, useForm, useApi, useModal, useAlert } from "hooks";
import { Icon } from "@material-ui/core";
import { formatAmount } from "utils";
import { sumBy, find } from "lodash";
import Big from "big.js";
import { InputAmount } from "components/field";
import grid from "styles/grid.module.scss";
import { ShiftStatus } from "enums";
import TooltipForm from "components/commons/tooltip-form/tooltip-form.module.js";
import initialState from "./net-cash.state";
import { updateShiftById } from "apis/shift-records.api";
import { SuccessModal } from "components/modals";

const ShiftDetailsNetCashSummary = ({
  shift,
  pageMode,
  employees,
  setEmployees,
  safeDrop,
  employeesToPay,
  setEmployeesToPay,
  variance,
  setVariance,
}) => {
  const successModal = useModal();
  const { expense } = shift;
  const status = shift?.shift.status;
  const totalExpenses = sumBy(expense, "amount") || 0;
  const { beginningCashBalance, shiftId, remarksCashBalance } = shift.shift;
  const { addAlert } = useAlert();

  const cashTransactions = shift?.transactionPaymentMethod?.cash || [];
  const netCashSales = Big(
    cashTransactions.reduce((sum, t) => Big(sum).add(t.finalAmount), 0),
  ).round(2);

  const initialFormState = useMemo(
    () => initialState(beginningCashBalance, remarksCashBalance),
    [beginningCashBalance, remarksCashBalance],
  );
  const { fields, modifyField, getFormValues } = useForm({
    initialState: initialFormState,
  });

  const formValues = getFormValues();
  const beginningChanged =
    Number(formValues.beginningCashBalance) !== Number(initialFormState.beginningCashBalance.value);
  const remarksChanged =
    String(formValues.remarksCashBalance || "") !==
    String(initialFormState.remarksCashBalance.value || "");

  const isFirstUpdate = !initialFormState.remarksCashBalance.value;
  const hasChanges = isFirstUpdate
    ? beginningChanged && remarksChanged
    : beginningChanged || remarksChanged;
  const confirmDisabled = !hasChanges;

  const updateShift = useApi({
    api: updateShiftById,
    handleOwnError: true,
    params: {
      shiftId,
    },
  });
  const [showTooltip, setShowTooltip] = useState(false);
  const [showRemarksTooltip, setShowRemarksTooltip] = useState(false);

  const totalSafeDrop = sumBy(safeDrop, "totalAmount") || 0;

  const [newCashOnHand, setNewCashOnHand] = useState(
    Number(Big(shift.shift.beginningCashBalance).add(netCashSales).minus(totalExpenses)),
  );

  const cashOnHand = newCashOnHand;

  useEffect(() => {
    setVariance(Number(Big(totalSafeDrop).minus(cashOnHand)));
  }, [totalSafeDrop, cashOnHand, setVariance]);

  const handleBeginningBalanceBlur = () => {
    setShowTooltip(true);
  };

  const handleCancel = () => {
    setShowTooltip(false);
  };

  const handleViewRemarks = () => {
    setShowRemarksTooltip(true);
  };

  const handleConfirm = async () => {
    setShowTooltip(false);
    try {
      const params = getFormValues();
      const total = Number(
        Big(totalSafeDrop)
          .minus(params.beginningCashBalance)
          .add(netCashSales)
          .minus(totalExpenses),
      );
      const payload = {
        beginningCashBalance: Number(params.beginningCashBalance),
        remarksCashBalance: params.remarksCashBalance,
      };
      if (total > 0) {
        payload.excess = total;
        payload.shortage = 0;
      } else if (total < 0) {
        payload.excess = 0;
        payload.shortage = Math.abs(total);
      } else {
        payload.excess = 0;
        payload.shortage = 0;
      }
      await updateShift.request(payload);
      // Recompute cashOnHand and variance after successful update
      const updatedCashOnHand = Number(
        Big(params.beginningCashBalance).add(netCashSales).minus(totalExpenses),
      );
      setNewCashOnHand(updatedCashOnHand);
      const newVariance = Number(Big(totalSafeDrop).minus(updatedCashOnHand));
      setVariance(newVariance);

      successModal.show();
    } catch (error) {
      return addAlert({
        title: locale.errorWhileUpdatingBeginningBalance,
        subtitle: error?.data?.message,
        type: "error",
        interval: 10000,
      });
    }
  };

  const formatNetCash = ({ data }) => {
    return [
      {
        label: locale.beginningBalance,
        value:
          pageMode === FormMode.Edit ? (
            <TooltipForm
              content={
                <div>
                  <div className={styles.tooltipContent}>
                    {locale.thisUpdateWillOnlyApplyinBOS}
                    <br />
                    <br />
                    {locale.areYouSureYouWantToUpdateBeginningBalance}
                  </div>
                  <Field
                    {...fields.remarksCashBalance}
                    required
                    labelClassName={styles.fieldLabel}
                    label={
                      <>
                        {locale.remarks}
                        <span style={{ color: "red", marginLeft: 2 }}>*</span>
                      </>
                    }
                  >
                    <TextField
                      multiline
                      required
                      {...fields?.remarksCashBalance}
                      onChange={modifyField}
                      rowsMax={3}
                    />
                  </Field>
                  <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
                    <Button outline style={{ marginRight: 8 }} onClick={handleCancel}>
                      {locale.cancel}
                    </Button>
                    <Button primary disabled={confirmDisabled} onClick={handleConfirm}>
                      {locale.confirm}
                    </Button>
                  </div>
                </div>
              }
              disabled={!showTooltip}
              visible={showTooltip}
            >
              <InputAmount
                {...fields?.beginningCashBalance}
                onChange={modifyField}
                innerLabel
                onBlur={handleBeginningBalanceBlur}
              />
            </TooltipForm>
          ) : (
            <div className={styles.viewBeginningBalance}>
              {(beginningCashBalance > 0 ? "+ " : beginningCashBalance < 0 ? "- ₱" : "") +
                formatAmount(beginningCashBalance)}
              {fields?.remarksCashBalance.value && (
                <TooltipForm
                  content={
                    <div className={styles.viewBeginningBalanceContainer}>
                      <Text strong color={"black"}>
                        {locale.remarks}
                      </Text>
                      <Text label color={"black"} className={styles.remarksText}>
                        {fields?.remarksCashBalance?.value}
                      </Text>
                    </div>
                  }
                  disabled={!showRemarksTooltip}
                  visible={showRemarksTooltip}
                >
                  <span
                    onMouseLeave={() => setShowRemarksTooltip(false)}
                    onMouseEnter={() => setShowRemarksTooltip(true)}
                    style={{ display: "inline-block" }}
                  >
                    <Button link onClick={handleViewRemarks}>
                      {locale.viewRemarks}
                    </Button>
                  </span>
                </TooltipForm>
              )}
            </div>
          ),
      },
      {
        label: locale.cashSales,
        value:
          (netCashSales > 0 ? "+ " : netCashSales < 0 ? "- ₱" : "") +
          formatAmount(netCashSales, ""),
      },
      {
        label: locale.expenses,
        value:
          (totalExpenses > 0 ? "- " : totalExpenses < 0 ? "₱" : "") + formatAmount(totalExpenses),
      },
      {
        label: locale.expectedCashOnHand,
        value: formatAmount(cashOnHand),
      },
      {
        label: locale.actualCashOnHand,
        value: formatAmount(totalSafeDrop),
      },
      {
        label: locale.variance,
        value:
          (variance > 0 ? "+ ₱" : variance < 0 ? "- ₱" : "") + formatAmount(Math.abs(variance), ""),
      },
    ];
  };

  const netCashSummaryData = formatNetCash({ data: shift?.shift });

  const addRow = () => {
    setEmployeesToPay([
      ...employeesToPay,
      {
        label: "",
        value: "",
        shoulderAmount: 0,
        amountCheck: false,
      },
    ]);
  };

  const onRemove = (i) => {
    const newForm = [...employeesToPay];
    newForm.splice(i, 1);
    setEmployeesToPay(newForm);
  };

  const tempList = [...employees];
  employees.forEach((a, i) => {
    if (a.name === "" || a.agency === "" || a.label === "") {
      tempList.splice(i, 1);
    }
  });

  const options = tempList.map((employee) => ({
    value: employee.name,
    label: employee.name,
  }));

  const onChangeAmount = (value, i) => {
    const newForm = [...employeesToPay];
    let inValid = true;
    newForm[i]["shoulderAmount"] = value;

    if (value.length > 0) {
      inValid = false;
    }
    newForm[i].amountCheck = inValid;

    setEmployeesToPay(newForm);
  };

  const onChangeName = (value, i) => {
    const newForm = [...employeesToPay];
    let inValid = false;
    if (find(employeesToPay, (ep) => ep.value === value)) {
      inValid = true;
    } else {
      const obj = value;
      newForm[i]["label"] = obj.name;
      newForm[i]["value"] = value;
    }

    newForm[i].nameCheck = inValid;

    setEmployeesToPay(newForm);
  };

  useMount(() => {
    if (Big(employeesToPay.length).eq(0)) {
      setEmployeesToPay([]);
      const newEmployeesToPay = [];
      employees.forEach((i) => {
        if (Big(i.shoulderAmount || 0).gt(0)) {
          newEmployeesToPay.push({
            label: i.name,
            value: i,
            shoulderAmount: i.shoulderAmount,
            nameCheck: false,
            amountCheck: false,
          });
        }
      });
      setEmployeesToPay(newEmployeesToPay);
    }
  });

  return (
    <>
      <Title small black>
        {locale.netCashSummary}
      </Title>
      <div className={styles.spacer10} />
      <div className={styles.netCashTable}>
        {netCashSummaryData && netCashSummaryData.length > 0 && (
          <table className={styles.table}>
            <tbody>
              {netCashSummaryData.map((item, i) => {
                return (
                  <tr key={i}>
                    <td>
                      <Text className={styles.label}>{item.label ?? ""}</Text>
                    </td>

                    <td>
                      <Text
                        className={
                          item.label === locale.variance
                            ? variance > 0
                              ? styles.positive
                              : variance < 0
                                ? styles.negative
                                : styles.content
                            : styles.content
                        }
                      >
                        {item.value ?? "0"}
                      </Text>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {pageMode === FormMode.View && (
        <>
          <div className={styles.spacer20} />
          {status !== ShiftStatus.Started && variance < 0 && (
            <div className={styles.container}>
              <div className={grid.row}>
                <div className={grid.column}>
                  <Text className={styles.label}>{locale.employeeToShoulderShortage}</Text>
                </div>
                <div className={grid.column}>
                  <Text className={styles.label}>{locale.amountToShoulder}</Text>
                </div>
              </div>
            </div>
          )}
          {status !== ShiftStatus.Started &&
            variance < 0 &&
            employees.map(
              (item, i) =>
                item.shoulderAmount !== 0 && (
                  <div className={styles.container}>
                    <div className={grid.row}>
                      <div className={grid.column}>
                        <Text className={styles.content}>{item.name}</Text>
                      </div>
                      <div className={grid.column}>
                        <Text className={styles.content}>{formatAmount(item.shoulderAmount)}</Text>
                      </div>
                    </div>
                  </div>
                ),
            )}
        </>
      )}

      {Big(variance).lt(0) && pageMode === FormMode.Edit ? (
        <table className={styles.tableView}>
          <tbody>
            {employeesToPay.map((item, i) => (
              <>
                {options.length > 0 && (
                  <tr key={i}>
                    <td style={{ paddingRight: "20px" }}>
                      <Field className={styles.content} label={locale.employeeToShoulderShortage}>
                        <Dropdown
                          error={item.nameCheck}
                          onChange={(value) => {
                            onChangeName(value, i);
                          }}
                          value={item.value}
                          options={options}
                        />
                      </Field>
                    </td>
                    <td className={styles.addOrEditTd}>
                      <Field className={styles.content} label={locale.amountToShoulder}>
                        <InputAmount
                          innerLabel
                          error={item.amountCheck}
                          label={locale.amount}
                          value={item.shoulderAmount}
                          onChange={(_, { value }) => {
                            onChangeAmount(value, i);
                          }}
                        />
                      </Field>
                    </td>
                    <td>
                      <div>
                        {i === 0 ? (
                          ""
                        ) : (
                          <span onClick={() => onRemove(i)} className={styles.remove}>
                            <Image src={RemoveIcon} />
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
          <div className={styles.spacer10} />
          <Button
            outline
            onClick={addRow}
            startIcon={Icon.Add}
            disabled={options.length === employeesToPay.length}
          >
            {locale.addEmployee}
          </Button>
        </table>
      ) : (
        <></>
      )}
      <div className={styles.spacer50} />

      <SuccessModal
        image={StocqClockIn}
        title={locale.updateBeginningBalance}
        content={locale.successMessageModalBeginningBalance}
        {...successModal}
        close={() => {
          successModal.close();
        }}
        primary={{
          text: locale.gotIt,
          onClick: () => {
            successModal.close();
          },
        }}
      />
    </>
  );
};

export default ShiftDetailsNetCashSummary;
